import { DATA_SERVICE_BASE_URL } from '../ApiService';
import { fetchWithAuth } from '../AuthService';

/**
 * Analytics API service for advanced analytics endpoints.
 * Supports funnel analysis, cohort retention, conversion tracking, and user journey patterns.
 */
export const AnalyticsApi = {
  /**
   * Analyze a multi-step conversion funnel.
   * 
   * @param {Object} request - Funnel analysis parameters
   * @param {string[]} request.steps - Ordered array of event types
   * @param {string} request.from - ISO timestamp for start date
   * @param {string} request.to - ISO timestamp for end date
   * @param {number} [request.userId] - Optional user filter
   * @param {boolean} [request.sessionBased=true] - Require all steps in same session
   * @returns {Promise<Object>} Funnel metrics with conversion rates
   */
  analyzeFunnel: async (request) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/funnel/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Funnel analysis failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get saved funnel definitions.
   */
  getSavedFunnels: async () => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/funnels`);
    if (!response.ok) {
      throw new Error(`Get funnels failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Save a funnel definition for reuse.
   */
  saveFunnel: async (request) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/funnels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Save funnel failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get cohort retention analysis.
   * @param {Object} request - Cohort retention parameters
   * @param {string} request.cohortType - SIGNUP, FIRST_LOGIN, FIRST_PURCHASE, FIRST_COURSE
   * @param {string} request.period - DAILY, WEEKLY, MONTHLY
   * @param {string} request.returnMetric - ANY_ACTIVITY, LOGIN, ENGAGEMENT, PURCHASE (optional)
   * @param {string} request.from - ISO datetime string
   * @param {string} request.to - ISO datetime string
   * @param {number} request.maxPeriods - Optional max periods to track
   */
  getCohortRetention: async (request) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/cohorts/retention`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Cohort retention failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get user journey patterns.
   * @param {Object} request - Journey pattern parameters
   * @param {number} request.minSupport - Minimum pattern frequency
   * @param {number} request.maxSteps - Maximum sequence length (2-10)
   * @param {string} request.from - ISO datetime string
   * @param {string} request.to - ISO datetime string
   * @param {string} request.category - Optional category filter
   * @param {boolean} request.includeAnonymous - Include anonymous users
   * @param {number} request.maxGapSeconds - Max time between events (seconds)
   */
  getJourneyPatterns: async (request) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/paths`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Journey patterns failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get conversion goals.
   */
  getConversionGoals: async () => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/goals`);
    if (!response.ok) {
      throw new Error(`Get goals failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Create a conversion goal.
   */
  createConversionGoal: async (request) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/goals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error(`Create goal failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get conversion metrics for a goal.
   */
  getGoalConversions: async (goalId, { from, to }) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/goals/${goalId}/conversions?${params}`);
    if (!response.ok) {
      throw new Error(`Goal conversions failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Delete (deactivate) a conversion goal.
   */
  deleteConversionGoal: async (goalId) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/goals/${goalId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      throw new Error(`Delete goal failed: ${response.status}`);
    }
  },

  // ─── Phase 4: Advanced Analytics Endpoints ──────────────────────────

  /**
   * Get document-level engagement metrics.
   * @param {string} from - ISO timestamp
   * @param {string} to - ISO timestamp
   * @param {number} [topN=20] - Max documents to return
   */
  getDocumentEngagement: async ({ from, to, topN = 20 }) => {
    const params = new URLSearchParams({ from, to, topN: String(topN) });
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/document-engagement?${params}`);
    if (!response.ok) {
      throw new Error(`Document engagement failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get automatic study funnel (DOCUMENT_OPENED → CHAT → QUIZ_STARTED → QUIZ_COMPLETED).
   * @param {string} from - ISO timestamp
   * @param {string} to - ISO timestamp
   */
  getStudyFunnel: async ({ from, to }) => {
    const params = new URLSearchParams({ from, to });
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/study-funnel?${params}`);
    if (!response.ok) {
      throw new Error(`Study funnel failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get feature adoption analytics.
   * @param {string} from - ISO timestamp
   * @param {string} to - ISO timestamp
   * @param {boolean} [timeSeries=false] - Return daily breakdown instead of summary
   */
  getFeatureAdoption: async ({ from, to, timeSeries = false }) => {
    const params = new URLSearchParams({ from, to, timeSeries: String(timeSeries) });
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/feature-adoption?${params}`);
    if (!response.ok) {
      throw new Error(`Feature adoption failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get learning session analytics from aggregated data.
   * @param {string} from - ISO timestamp
   * @param {string} to - ISO timestamp
   * @param {number} [userId] - Optional user filter
   * @param {number} [page=0] - Page number (0-based)
   * @param {number} [pageSize=20] - Page size
   */
  getLearningSessionAnalytics: async ({ from, to, userId, page = 0, pageSize = 20 }) => {
    const params = new URLSearchParams({ from, to, page: String(page), pageSize: String(pageSize) });
    if (userId) params.set('userId', String(userId));
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/learning-sessions?${params}`);
    if (!response.ok) {
      throw new Error(`Learning sessions failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get user learning profiles from materialized view.
   * @param {Object} params
   * @param {string} [params.learningStyle] - Filter: quiz_heavy, chat_heavy, reading_heavy, balanced
   * @param {string} [params.subscriptionStatus] - Filter by subscription status
   * @param {string} [params.sortBy='engagementScore'] - Sort field
   * @param {string} [params.sortDir='DESC'] - Sort direction
   * @param {number} [params.page=0] - Page number
   * @param {number} [params.pageSize=20] - Page size
   */
  getUserLearningProfiles: async ({ learningStyle, subscriptionStatus, sortBy = 'engagementScore', sortDir = 'DESC', page = 0, pageSize = 20 } = {}) => {
    const params = new URLSearchParams({ sortBy, sortDir, page: String(page), pageSize: String(pageSize) });
    if (learningStyle) params.set('learningStyle', learningStyle);
    if (subscriptionStatus) params.set('subscriptionStatus', subscriptionStatus);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/user-profiles?${params}`);
    if (!response.ok) {
      throw new Error(`User profiles failed: ${response.status}`);
    }
    return response.json();
  },

  // ─── Aggregated Metrics Explorer ───────────────────────────────────

  /**
   * Fetch pre-aggregated event metrics from event_metrics_hourly.
   * Returns { timeSeries, pivot, summary, performance }.
   * @param {Object} params
   * @param {string} params.from - ISO-8601 start
   * @param {string} params.to - ISO-8601 end
   * @param {string} [params.groupBy='event_type'] - Pivot dimension
   * @param {string} [params.timeBucket='hourly'] - Time rollup
   * @param {string} [params.eventType] - ILIKE filter
   * @param {string} [params.category] - ILIKE filter
   * @param {string} [params.action] - ILIKE filter
   * @param {string} [params.entityType] - ILIKE filter
   */
  getAggregatedEventMetrics: async ({ from, to, groupBy = 'event_type', timeBucket = 'hourly', eventType, category, action, entityType } = {}) => {
    const params = new URLSearchParams({ from, to, groupBy, timeBucket });
    if (eventType) params.set('eventType', eventType);
    if (category) params.set('category', category);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/aggregated/events?${params}`);
    if (!response.ok) {
      throw new Error(`Aggregated event metrics failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch GPT cost/usage time series from gpt_metrics_hourly.
   * Returns { timeSeries, performance }.
   */
  getAggregatedGptMetrics: async ({ from, to, timeBucket = 'daily', model, promptType } = {}) => {
    const params = new URLSearchParams({ from, to, timeBucket });
    if (model) params.set('model', model);
    if (promptType) params.set('promptType', promptType);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/aggregated/gpt?${params}`);
    if (!response.ok) {
      throw new Error(`Aggregated GPT metrics failed: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch payment metrics from payment_metrics_daily.
   * Returns { timeSeries, performance }.
   */
  getAggregatedPaymentMetrics: async ({ from, to, paymentEventType, provider } = {}) => {
    const params = new URLSearchParams({ from, to });
    if (paymentEventType) params.set('paymentEventType', paymentEventType);
    if (provider) params.set('provider', provider);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/analytics/aggregated/payments?${params}`);
    if (!response.ok) {
      throw new Error(`Aggregated payment metrics failed: ${response.status}`);
    }
    return response.json();
  }
};
